import { supabase } from './supabase'

export const PRODUCT_IMAGE_BUCKET = 'product-images'

// Optimize (resize ≤1600px + WebP q0.8) and upload a product photo, returning the
// public URL. Throws a user-friendly Error on any failure so callers can toast it
// once and never leave a half-set state. Shared by ProductPanel, the inline
// row-thumbnail uploader in ProductsTab, and the POS tile image-add.
export async function optimizeAndUploadProductImage(file: File, tenantId: string): Promise<string> {
  // Anti-abuse ceiling only (NOT a 2MB cap) — phone photos are routinely
  // 3-8MB and the resize below shrinks the stored file to a fraction anyway.
  if (file.size > 15 * 1024 * 1024) {
    throw new Error('Image is too large (max 15MB). Please pick a smaller photo.')
  }

  // Decode the file into an image element (revoke the object URL once loaded).
  const objectUrl = URL.createObjectURL(file)
  let img: HTMLImageElement
  try {
    img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new window.Image()
      el.onload = () => resolve(el)
      el.onerror = () => reject(new Error('decode'))
      el.src = objectUrl
    })
  } catch {
    throw new Error("That file couldn't be read as an image. Please try another.")
  } finally {
    URL.revokeObjectURL(objectUrl)
  }

  // Resize so the longest side is ≤ 1600px, preserving aspect ratio.
  const MAX = 1600
  const longest = Math.max(img.width, img.height)
  const scale = longest > MAX ? MAX / longest : 1
  const w = Math.round(img.width * scale)
  const h = Math.round(img.height * scale)
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error("Couldn't process that image. Please try another.")
  ctx.drawImage(img, 0, 0, w, h)

  // Encode to WebP at 0.8 quality — this is the ONLY thing we store.
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/webp', 0.8),
  )
  if (!blob) throw new Error("Couldn't process that image. Please try another.")

  // Short random suffix so two images uploaded in the same millisecond (multi-image
  // gallery add) never collide onto one path and silently overwrite each other under
  // upsert:true. Shared helper — POS quick-add / inventory row / panel all benefit;
  // existing stored URLs are unaffected (only new uploads get the longer name).
  const rand = Math.random().toString(36).slice(2, 8)
  const filename = `${tenantId}/${Date.now()}-${rand}.webp`
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase as any).storage
    .from(PRODUCT_IMAGE_BUCKET)
    .upload(filename, blob, { upsert: true, contentType: 'image/webp' })
  if (error) {
    console.error('Image upload error:', error)
    if (error.message?.includes('Bucket not found') || error.message?.includes('bucket')) {
      throw new Error('Storage bucket not configured. Run the SQL in CLAUDE.md to create it.')
    }
    throw new Error('Image upload failed: ' + error.message)
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: urlData } = (supabase as any).storage.from(PRODUCT_IMAGE_BUCKET).getPublicUrl(filename)
  const url = urlData?.publicUrl as string | undefined
  if (!url) throw new Error("Couldn't process that image. Please try another.")
  return url
}

// Extract the in-bucket object path from a public storage URL, e.g.
// ".../object/public/product-images/<tenant>/<ts>.webp" → "<tenant>/<ts>.webp".
export function productImagePathFromUrl(publicUrl: string): string | null {
  const marker = `/storage/v1/object/public/${PRODUCT_IMAGE_BUCKET}/`
  const idx = publicUrl.indexOf(marker)
  if (idx === -1) return null
  const path = publicUrl.slice(idx + marker.length).split('?')[0]
  return path ? decodeURIComponent(path) : null
}

// Best-effort removal of a previously stored product image on replace. Only
// touches this tenant's own objects, and never throws — a failed delete must
// never block the replace that already succeeded.
export async function removeProductImageObject(publicUrl: string, tenantId: string): Promise<void> {
  try {
    const path = productImagePathFromUrl(publicUrl)
    if (!path || !path.startsWith(`${tenantId}/`)) return
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (supabase as any).storage.from(PRODUCT_IMAGE_BUCKET).remove([path])
  } catch {
    // ignore — storage hygiene only
  }
}
